import { useEffect, useState } from "react";
import { RippCore } from "../components/v2/RippCore";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Activity, Brain, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/* Hidden admin/debug console for the Kimi layer (§25). Login-gated route /admin/ai. */

const Box = ({ title, children, testid }) => (
  <div data-testid={testid} className="rounded-lg border border-white/[0.07] bg-[#080B11]/80 p-3">
    <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-2">{title}</div>
    {children}
  </div>
);

const Row = ({ k, v, col = "text-zinc-200" }) => (
  <div className="flex items-baseline justify-between gap-3 py-[2px]">
    <span className="text-[9.5px] font-mono uppercase tracking-[0.1em] text-zinc-500">{k}</span>
    <span className={`text-[10.5px] font-mono tabular-nums truncate ${col}`}>{String(v ?? "—")}</span>
  </div>
);

export default function AdminAi() {
  const nav = useNavigate();
  const [dbg, setDbg] = useState(null);
  const [status, setStatus] = useState(null);
  const [perf, setPerf] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => {
    api.aiDebug().then(setDbg).catch(() => {});
    api.aiStatus().then(setStatus).catch(() => {});
    api.aiPerformance().then(setPerf).catch(() => {});
  };
  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, []);

  const ping = async () => {
    setBusy(true); setMsg("");
    try { const r = await api.aiPing(); setMsg(r.ok ? `Kimi OK · ${r.latency_ms}ms · ${r.model}` : `FAILED: ${r.error}`); }
    catch (e) { setMsg(`FAILED: ${e?.message || "error"}`); }
    finally { setBusy(false); load(); }
  };
  const analyze = async () => {
    setBusy(true); setMsg("");
    try { const r = await api.aiAnalyze(status?.focus || "MNQ"); setMsg(`queued ${r.agents?.join(", ")}`); }
    catch (e) { setMsg(`FAILED: ${e?.response?.data?.detail || e?.message}`); }
    finally { setBusy(false); load(); }
  };

  const k = dbg?.kimi || {};
  const st = dbg?.stats || {};

  return (
    <div className="min-h-screen r2-bg text-zinc-200 p-5" data-testid="admin-ai">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <a href="/v2" data-testid="admin-core" className="grid h-9 w-9 place-items-center"><RippCore mode={status?.kimi?.connected ? "scanning" : "offline"} agents={[]} size={40} compact /></a>
          <button onClick={() => nav("/v2")} data-testid="admin-back"
            className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.16em] text-zinc-400 hover:text-white">
            <ArrowLeft size={12} /> Terminal
          </button>
          <div>
            <div className="text-sm font-black tracking-[0.16em] text-white">KIMI AGENT CONSOLE</div>
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500">
              internal · shadow mode {String(dbg?.shadow_mode)} · execution access none
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={ping} disabled={busy} data-testid="admin-ping"
            className="flex items-center gap-1.5 rounded border border-blue-500/40 bg-blue-500/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-blue-300 hover:bg-blue-500/20 disabled:opacity-40">
            <Activity size={11} /> ping kimi
          </button>
          <button onClick={analyze} disabled={busy} data-testid="admin-analyze"
            className="flex items-center gap-1.5 rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40">
            <Brain size={11} /> analyze {status?.focus || ""}
          </button>
          <button onClick={load} className="rounded border border-white/10 p-1.5 text-zinc-400 hover:text-white">
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {msg && (
        <div data-testid="admin-msg" className="mb-3 rounded border border-white/10 bg-black/50 px-3 py-2 text-[10.5px] font-mono text-zinc-300">
          {msg}
        </div>
      )}
      {k.unavailable_reason && (
        <div data-testid="admin-unavailable" className="mb-3 flex items-center gap-2 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[10.5px] font-mono text-amber-300">
          <AlertTriangle size={12} /> {k.unavailable_reason}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Box title="Connection" testid="admin-connection">
          <Row k="Model" v={k.model} />
          <Row k="Base URL" v={k.base_url} />
          <Row k="Key configured" v={String(!!k.enabled)} col={k.enabled ? "text-emerald-400" : "text-red-400"} />
          <Row k="Connected" v={String(k.connected)} col={k.connected ? "text-emerald-400" : "text-red-400"} />
          <Row k="Last latency" v={k.last_latency_ms ? `${k.last_latency_ms} ms` : "—"} />
          <Row k="Last agent" v={k.last_agent} />
          <Row k="Last error" v={k.last_error} col="text-red-400" />
        </Box>

        <Box title="Calls & tokens" testid="admin-calls">
          <Row k="Calls" v={k.calls} />
          <Row k="Failures" v={k.failures} col={k.failures ? "text-red-400" : "text-zinc-200"} />
          <Row k="JSON retries" v={k.json_retries} />
          <Row k="Timeouts" v={k.timeouts} />
          <Row k="Prompt tokens" v={k.tokens?.prompt} />
          <Row k="Completion tokens" v={k.tokens?.completion} />
          <Row k="Cached tokens" v={k.tokens?.cached} />
          <Row k="Calls last hour" v={`${dbg?.calls_last_hour ?? 0} / ${dbg?.budget_per_hour ?? "—"}`} />
        </Box>

        <Box title="Orchestrator" testid="admin-orchestrator">
          <Row k="Focus symbol" v={dbg?.focus} />
          <Row k="Queue depth" v={dbg?.queue_depth} />
          <Row k="Active calls" v={(dbg?.running || []).join(", ") || "idle"} />
          <Row k="Runs" v={st.runs} />
          <Row k="Specialist calls" v={st.specialist_calls} />
          <Row k="Rejected JSON" v={st.rejected_json} />
          <Row k="Skipped (cooldown)" v={st.skipped_cooldown} />
          <Row k="Skipped (dedup)" v={st.skipped_dedup} />
          <Row k="Skipped (low delta)" v={st.skipped_insignificant} />
          <Row k="Cooldown / min delta" v={`${dbg?.cooldown_seconds}s / ${dbg?.min_significance}`} />
        </Box>

        <Box title="Recent events" testid="admin-events">
          <div className="space-y-0.5 max-h-56 overflow-y-auto">
            {(dbg?.recent_events || []).map((e) => (
              <div key={e.id} className="flex items-center gap-2 text-[9.5px] font-mono">
                <span className="text-zinc-600">{new Date(e.ts * 1000).toLocaleTimeString()}</span>
                <span className="text-cyan-400">{e.type}</span>
                <span className="text-zinc-400">{e.symbol}</span>
              </div>
            ))}
            {!(dbg?.recent_events || []).length && <span className="text-[9.5px] font-mono text-zinc-600">no events yet</span>}
          </div>
        </Box>

        <Box title="Last consensus" testid="admin-consensus">
          <pre className="max-h-56 overflow-auto text-[9px] font-mono text-zinc-400 whitespace-pre-wrap">
            {JSON.stringify(dbg?.consensus || {}, null, 1)}
          </pre>
        </Box>

        <Box title={`Agent leaderboard · adaptive weights ${perf?.adaptive_weights_enabled ? "ON" : "OFF"}`} testid="admin-performance">
          <Row k="Consensus samples" v={perf?.consensus?.samples ?? 0} />
          <Row k="Consensus accuracy" v={perf?.consensus?.directional_accuracy != null ? `${perf.consensus.directional_accuracy}%` : "—"} />
          <div className="mt-2 space-y-1.5" data-testid="agent-leaderboard">
            {(perf?.leaderboard || Object.keys(perf?.agents || {})).map((name, i) => {
              const a = perf.agents[name];
              const mult = a.weight_multiplier ?? 1;
              const tone = mult > 1 ? "var(--r2-emerald)" : mult < 1 ? "var(--r2-red)" : "var(--r2-text2)";
              const thin = (a.samples || 0) < (perf?.min_samples ?? 20);
              return (
                <div key={name} className="rounded border border-[var(--r2-border)] px-2 py-1.5" data-testid={`leaderboard-row-${name}`}>
                  <div className="flex items-center gap-2 text-[10px] font-mono">
                    <span className="w-4 text-[var(--r2-text2)]">#{i + 1}</span>
                    <span className="text-zinc-100 font-bold tracking-[0.08em] uppercase">{name.replace(/_agent$/, "").replace(/_/g, " ")}</span>
                    <span className="ml-auto font-bold r2-num" style={{ color: tone }} data-testid={`leaderboard-mult-${name}`}>×{mult.toFixed(2)}</span>
                    <span className="text-[var(--r2-text2)] r2-num">w {a.base_weight} → {a.effective_weight}</span>
                  </div>
                  <div className="mt-1 grid grid-cols-4 gap-2 text-[8.5px] font-mono">
                    <span><span className="text-[var(--r2-text2)]">acc </span><span style={{ color: a.directional_accuracy >= 55 ? "var(--r2-emerald)" : a.directional_accuracy < 45 ? "var(--r2-red)" : "var(--r2-text1)" }}>{a.directional_accuracy}%</span></span>
                    <span><span className="text-[var(--r2-text2)]">calib </span><span style={{ color: Math.abs(a.confidence_calibration) > 15 ? "var(--r2-amber)" : "var(--r2-text1)" }}>{a.confidence_calibration > 0 ? "+" : ""}{a.confidence_calibration}</span></span>
                    <span><span className="text-[var(--r2-text2)]">fp </span>{a.false_positive_rate}%</span>
                    <span><span className="text-[var(--r2-text2)]">n </span>{a.samples}{thin ? <span className="text-[var(--r2-amber)]"> · warming up</span> : ""}</span>
                  </div>
                  <div className="mt-1 h-1 rounded bg-[var(--r2-border)] overflow-hidden"><div className="h-full transition-[width] duration-700" style={{ width: `${Math.min(100, a.directional_accuracy)}%`, background: tone }} /></div>
                </div>
              );
            })}
            {!Object.keys(perf?.agents || {}).length && <span className="text-[9.5px] font-mono text-zinc-600">no scored samples yet</span>}
          </div>
          <div className="mt-2 text-[9px] font-mono text-zinc-600">{perf?.note}</div>
        </Box>
      </div>
    </div>
  );
}
